import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useRouteMatch } from "react-router";
import { KButton, KSelect, KSelectAutocomplete } from "@kyburz/kdesign-system";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import KLabel from "../../../../components/ui/KLabel";
import JsonTextInput from "../../../../components/JsonTextInput";
import useSetState from "../../../../hooks/useSetState";
import {
  getDevice,
  updateDevice,
  deleteDevice,
} from "../../../../apiCalls/config/devices";
import { setAlert, setCustomer, resetUser } from "../../../../redux/actions/actions";
import { getCustomers } from "../../../../apiCalls/auth";
import { getFullTemplates } from "../../../../apiCalls/config/fullTemplates";
import { getVehicle } from "../../../../apiCalls/admin/vehicles";
import { hardwareTypes } from "../../../../utils/hardwareTypes";
import { useStyles } from "./styles";

const DeviceView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentCustomer: string = useSelector(
    (state: any) => state?.customer?.currentCustomer?.shortname,
  );
  const history: any = useHistory();
  const match = useRouteMatch({
    path: "/config/devices/device/:imei",
    strict: true,
    sensitive: true,
  });
  const { imei } = match?.params as any;

  const [
    {
      customer,
      template,
      hardware,
      sim_type,
      template_overwrite,
      transformation_overwrite,
      variables,
      vehicle_uuid,
      formControlCustomers,
      formControlTemplates,
      vehicleName,
      fullCustomers,
    },
    setState,
  ] = useSetState({
    customer: "",
    template: "",
    hardware: "",
    sim_type: "",
    template_overwrite: {},
    transformation_overwrite: {},
    variables: {},
    vehicle_uuid: "",
    formControlCustomers: [],
    formControlTemplates: [],
    vehicleName: "",
    fullCustomers: [],
  });

  useEffect(() => {
    getDevice(imei)
      .then((res) =>
        res && res.data && res.data
          ? setState({
              customer: res.data.customer,
              template: res.data.template,
              hardware: res.data.hardware_type,
              template_overwrite: res.data.template_overwrite,
              transformation_overwrite: res.data.transformation_overwrite,
              variables: res.data.variables,
              vehicle_uuid: res.data.vehicle_uuid,
              sim_type: res.data.sim_type,
            })
          : null,
      )
      .catch((e) => {
        history.push("/config/devices");
        dispatch(setAlert("This device does not exist", "error"));
        console.error(e);
      });
    getCustomers().then((res) => {
      setState({
        formControlCustomers: res.data
          .filter((el) => el._id !== null)
          .map((el) => ({ label: el.shortname })),
        fullCustomers: res.data,
      });
    });
    getFullTemplates("", 1, null)
      .then((res) => {
        setState({
          formControlTemplates: res.data.map((el) => ({ label: el.name })),
        });
      })
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    if (customer && vehicle_uuid) {
      getVehicle(customer, vehicle_uuid).then((res) => {
        if (res.data) {
          const {
            data: { data },
          } = res;
          const name = data.name || "Frontend Vehicle Name not defined";
          setState({ vehicleName: name });
        } else {
          setState({ vehicleName: "Doesn't exist as frontend vehicle" });
        }
      });
    }
  }, [customer, vehicle_uuid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const res = await updateDevice(
      customer,
      imei,
      template,
      hardware,
      sim_type,
      template_overwrite,
      transformation_overwrite,
      variables,
    );
    if (res.errors) {
      setState({ errors: res.errors });
    } else if (res.detail) {
      dispatch(setAlert(res.detail, "error"));
    } else {
      setState({
        customer: res.data.customer,
        template: res.data.template,
        hardware: res.data.hardware_type,
        sim_type: res.data.sim_type,
        template_overwrite: res.data.template_overwrite,
        transformation_overwrite: res.data.transformation_overwrite,
        variables: res.data.variables,
        vehicle_uuid: res.data.vehicle_uuid,
      });
      dispatch(setAlert("Saved successfully", "success"));
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    const res = await deleteDevice(imei);
    if (res.detail) {
      dispatch(setAlert(res.detail, "error"));
    } else {
      history.push("/config/devices");
      dispatch(setAlert("Device deleted", "success"));
    }
  };

  const handleJsonChangeTemplateOverride = (code) =>
    setState({ template_overwrite: code });
  const handleJsonChangeTransformationOverwrite = (code) =>
    setState({ transformation_overwrite: code });
  const handleJsonChangeVariables = (code) => setState({ variables: code });
  const handleSelect = (value, name) => setState({ [name]: value.label });
  const handleSelectAutoComplete = (name, value) => {
    setState({ [name]: value.label });
  };

  const handleCustomerChange = () => {
    if (currentCustomer !== customer) {
      const fullCustomer =
        fullCustomers.filter((x) => x.shortname === customer)[0] || null;
      localStorage.setItem("currentCustomer", JSON.stringify(fullCustomer));
      dispatch(setCustomer(fullCustomer));
      dispatch(resetUser());
    }
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={3} style={{ margin: 0 }}>
        <Grid item xs>
          <Paper className={classes.paper} style={{ minWidth: "632px" }}>
            <Typography variant="body1" style={{ color: "#383F51" }}>
              General Information
            </Typography>
            <Divider />
            <div style={{ display: "flex", alignItems: "center" }}>
              <KLabel
                label="IMEI"
                value={imei}
                style={{ marginRight: "24px", width: 240 }}
              />
              <div style={{ width: "100%" }} />
              <KSelect
                label="SIM Type"
                name="sim_type"
                value={sim_type}
                onSelect={handleSelect}
                elements={[
                  { label: "None" },
                  { label: "Local" },
                  { label: "Global" },
                ]}
                style={{ width: "240px" }}
              />
            </div>
            <div className={classes.dropdowns}>
              <KSelect
                label="Template"
                name="template"
                value={template}
                onSelect={handleSelect}
                elements={formControlTemplates}
                style={{ width: "100%" }}
              />
              <KSelectAutocomplete
                label="Customer"
                name="customer"
                value={customer}
                onChange={handleSelectAutoComplete}
                elements={formControlCustomers}
                style={{ width: "100%" }}
                width="100%"
              />
              <KSelect
                onSelect={handleSelect}
                elements={hardwareTypes}
                label="Hardware Type"
                name="hardware"
                value={hardware}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ display: "flex" }}>
              <KLabel
                label="Connected Backend Vehicle"
                value={vehicle_uuid}
                style={{ marginRight: "24px" }}
                link={`/config/vehicles/${vehicle_uuid}`}
              />
              <KLabel
                label="Connected Frontend Vehicle"
                value={vehicleName}
                style={{ marginRight: "24px" }}
                onLinkClick={handleCustomerChange}
                link={
                  vehicleName === "Doesn't exist as frontend vehicle"
                    ? ""
                    : `/admin/fleet/vehicles/${vehicle_uuid}`
                }
              />
            </div>
            <div>
              <JsonTextInput
                label="Template overwrite"
                value={template_overwrite}
                handleSubmit={handleJsonChangeTemplateOverride}
                style={{ display: "inline-block", marginRight: "24px" }}
              />
              <JsonTextInput
                label="Transformation overwrite"
                value={transformation_overwrite}
                handleSubmit={handleJsonChangeTransformationOverwrite}
                style={{ display: "inline-block" }}
              />
              <JsonTextInput
                label="Variables"
                value={variables}
                handleSubmit={handleJsonChangeVariables}
              />
            </div>
            <Divider style={{ marginTop: "16px" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <KButton
                  children="Delete"
                  theme="danger"
                  size="medium"
                  onClick={handleDelete}
                />
              </div>
              <div>
                <KButton
                  children="Cancel"
                  theme="secondary"
                  size="medium"
                  onClick={() => history.goBack()}
                  style={{ display: "inline-block", marginRight: "16px" }}
                />
                <KButton
                  children="Save"
                  theme="primary"
                  size="medium"
                  onClick={handleSubmit}
                  style={{ display: "inline-block" }}
                />
              </div>
            </div>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default DeviceView;
